<template>
  <div v-if="imageUrl" :class="{ container: !fullWidth }" :style="backgroundImageStyle">
    <b-container>
      <b-row :class="{ 'reverse-row': isRight }">
        <b-col :style="imagePosition">
          <Video
            v-if="videoPath || videoIsEmbedded"
            :is-proportional="true"
            :path="videoPath"
            :options="options"
          />
          <img v-else :src="imageUrl" :alt="imageAlt" class="image img-fluid" loading="lazy" />
        </b-col>
        <b-col v-if="title || text || (buttonText && buttonPath)" class="text-container">
          <b-row class="text-content" :style="textColorStyle">
            <b-col>
              <prismic-rich-text v-if="title" :field="title" class="title"></prismic-rich-text>
              <prismic-rich-text
                v-if="parsedText"
                :field="parsedText"
                class="text"
              ></prismic-rich-text>
              <Button
                v-if="buttonText && buttonPath"
                :to="path"
                :blank="blank"
                v-bind="buttonProps"
                :size="buttonSize"
                @click="showModal"
              >
                {{ buttonText }}
              </Button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import Button from 'UI/Button';
import { msToHMS } from '~/utils/common.js';
import Video from '@/components/Common/Video.vue';

export default {
  components: {
    Button,
    Video
  },
  props: {
    slice: {
      type: Object,
      required: true
    },
    isRight: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      buttonBlank: false,
      buttonPath: '',
      buttonText: '',
      imageAlt: '',
      imageUrl: '',
      backgroundImageUrl: '',
      openModal: false,
      text: '',
      textParsed: '',
      timerId: 0,
      timeDiff: 0,
      timeRemaining: '',
      title: '',
      textColor: '#707070',
      videoPath: '',
      videoIsEmbedded: false,
      imageVerticalAlign: '',
      buttonBackgroundTransparent: false,
      buttonTextColor: '',
      buttonBackgroundColor1: '',
      buttonBackgroundColor2: '',
      borderRounded: false,
      internalPadding: true,
      externalMargin: false,
      endDate: ''
    };
  },
  computed: {
    blank() {
      return !this.openModal ? this.buttonBlank : false;
    },
    path() {
      return !this.openModal ? this.buttonPath : '';
    },
    backgroundImageStyle() {
      return this.backgroundImageUrl
        ? `padding: ${this.internalPadding ? '20px 30px' : '0px'};
          margin: ${this.externalMargin ? '5px 25px' : '0px'}; 
          border-radius: 5px; 
          background-image: url(${this.backgroundImageUrl});
          background-size: cover; 
          border-radius: ${this.borderRounded ? '25px' : '0'};`
        : '';
    },
    textColorStyle() {
      return `color: ${this.textColor}`;
    },
    imagePosition() {
      return this.imageVerticalAlign == 'center'
        ? 'margin: auto 0'
        : this.imageVerticalAlign == 'bottom'
        ? 'margin-top: auto'
        : '';
    },
    buttonBackgroundColor() {
      return (
        'linear-gradient(90deg,' + this.buttonBackgroundColor1 + ' , ' + this.buttonBackgroundColor2
      );
    },
    buttonHasGradient() {
      return (
        this.buttonBackgroundColor1 &&
        this.buttonBackgroundColor2 &&
        !this.buttonBackgroundTransparent
      );
    },
    buttonProps() {
      const props = this.buttonHasGradient
        ? {
            gradient: this.buttonBackgroundColor,
            withGradient: true
          }
        : this.buttonBackgroundTransparent
        ? {
            shape: 'outline',
            color: 'transparent'
          }
        : {};
      props.fontColor = this.buttonTextColor;
      return props;
    },
    parsedText() {
      if (this.text) {
        const aux = [];
        aux[0] = { ...this.text[0] };
        aux[0].text = `${this.text[0].text} ${this.timeRemaining}`;
        return aux;
      } else return '';
    }
  },
  created() {
    const { primary } = this.slice;

    this.buttonBlank = primary.button_blank;
    this.buttonPath = primary.button_path[0]?.text;
    this.buttonText = primary.button_text[0]?.text;
    this.buttonBackgroundTransparent = primary.button_background_transparent;
    this.buttonTextColor = primary.button_text_color;
    this.buttonSize = primary.button_size;
    this.buttonBackgroundColor1 = primary.button_background_color1;
    this.buttonBackgroundColor2 = primary.button_background_color2;

    this.imageAlt = primary.image.alt;
    this.imageUrl = primary.image.url;
    this.backgroundImageUrl = primary.background_image.url;
    this.openModal = primary.open_modal;
    this.text = primary.text[0]?.text ? primary.text : '';
    this.endDate = primary.end_date;
    if (this.text) {
      this.textParsed = [];
      this.textParsed[0] = { ...this.text[0] };
    }
    this.title = primary.title1[0]?.text ? primary.title1 : '';
    this.textColor = primary.text_color;
    this.fullWidth = primary.full_width;
    this.videoPath = primary.video_path;
    this.videoIsEmbedded = primary.video_is_embedded;

    this.borderRounded = primary.border_rounded;
    this.internalPadding = primary.internal_padding;
    this.externalMargin = primary.external_margin;

    const videoPoster = primary.video_poster;

    if (this.videoIsEmbedded) {
      this.options = {
        language: 'en',
        playbackRates: [1.0],
        sources: [
          {
            type: 'video/mp4',
            src: this.videoPath
          }
        ],
        poster: videoPoster ? videoPoster : ''
      };
      this.videoPath = '';
    }
    this.imageVerticalAlign = primary.image_vertical_align;
    if (this.endDate) {
      const formatedDate = this.endDate.slice(0, this.endDate.length - 5);
      const endDate = new Date(formatedDate);
      const diff = endDate.getTime() - new Date().getTime();
      this.timeDiff = diff > 0 ? diff : 0;
    }
  },
  mounted() {
    if (this.timeDiff) {
      this.startInterval();
    } else if (this.endDate) {
      this.timeRemaining = '00:00:00';
    }
  },
  beforeDestroy() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
  },
  methods: {
    showModal() {
      if (this.openModal) {
        this.$bvModal.show(this.buttonPath);
      }
    },
    startInterval() {
      this.timerId = setInterval(() => {
        this.timeDiff = this.timeDiff - 1000;
        this.timeRemaining = msToHMS(this.timeDiff);
      }, 1000);
    }
  }
};
</script>

<style lang="scss" scoped>
.reverse-row {
  flex-direction: row-reverse;
}
.image {
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.title {
  text-align: left;
  font-size: 32px;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0px;
  opacity: 1;
  padding-bottom: 20px;
}

.text {
  text-align: left;
  font-size: 16px;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0px;
  opacity: 1;
  padding-bottom: 20px;
  display: flex;
  flex-wrap: nowrap;
}

.text-container {
  display: flex;
  align-items: center;
  @include lg {
    flex-basis: auto;
  }
}

.text-content {
  padding: 20px;
}
</style>
